import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { hrApi } from "../../services/hrApi";
import HRPersonHeader from "../../components/hr/HRPersonHeader";
import HRProfileTabs from "../../components/hr/HRProfileTabs";
import { useAccess } from "../../context/AccessContext";
import { hasModule, hasPermission } from "../../utils/accessHelpers";



export default function HRPersonProfile() {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [person, setPerson] = useState(null);
  const [employment, setEmployment] = useState([]);
  const [notes, setNotes] = useState([]);

  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const [showEmploymentModal, setShowEmploymentModal] = useState(false);
  const [savingEmployment, setSavingEmployment] = useState(false);
  const [closingEmploymentId, setClosingEmploymentId] = useState(null);

  const { access, loading: accessLoading } = useAccess();

  const canSeeHR = hasModule(access, "hr");
  const canReadPeople = hasPermission(access, "hr.people.read");
  const canWritePeople = hasPermission(access, "hr.people.write");

  const [searchingUsers, setSearchingUsers] = useState(false);
  const [systemUserQuery, setSystemUserQuery] = useState("");
  const [systemUsers, setSystemUsers] = useState([]);
  const [linkingUser, setLinkingUser] = useState(false);

  const [employmentForm, setEmploymentForm] = useState({
  employment_type: "employee",
  contract_type: "full_time",
  employment_status: "active",
  start_date: "",
  end_date: "",
  probation_end_date: "",
  work_capacity_percent: 100,
  working_hours_per_week: 40,
  salary_type: "",
  currency: "",
  gross_salary: "",
  net_salary: "",
  cost_center_code: "",
  legal_entity: "",
  office_location: "",
  work_mode: "onsite",
  active_flag: 1,
  notes: "",
  });

  const [closeEmploymentForm, setCloseEmploymentForm] = useState({
  end_date: "",
  notes: "",
  });

  const [documents, setDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false);

  const [documentForm, setDocumentForm] = useState({
  document_type: "employment_contract",
  title: "",
  file_url: "",
  file_name: "",
  issue_date: "",
  expiry_date: "",
  visibility_scope: "hr_only",
  is_confidential: 0,
  notes: "",
  });

  const [uploadingFile, setUploadingFile] = useState(false);

  const [roles, setRoles] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [savingRole, setSavingRole] = useState(false);

  const [roleForm, setRoleForm] = useState({
  role_key: "",
  role_name: "",
  role_scope: "system",
  valid_from: "",
  valid_to: "",
  active_flag: 1,
  notes: "",
  });

function UIInput(props) {
  return (
    <input
      {...props}
      className={`w-full border border-gray-300 rounded-xl px-3 py-2
        bg-white text-black
        dark:bg-neutral-900 dark:text-white dark:border-neutral-700
        ${props.className || ""}`}
    />
  );
}

function UISelect(props) {
  return (
    <select
      {...props}
      className={`w-full border border-gray-300 rounded-xl px-3 py-2
        bg-white text-black
        dark:bg-neutral-900 dark:text-white dark:border-neutral-700
        ${props.className || ""}`}
    >
      {props.children}
    </select>
  );
}

function UITextarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full border border-gray-300 rounded-xl px-3 py-2
        bg-white text-black
        dark:bg-neutral-900 dark:text-white dark:border-neutral-700
        ${props.className || ""}`}
    />
  );
}

async function handleSearchSystemUsers() {
  try {
    setSearchingUsers(true);
    const res = await hrApi.searchSystemUsers(systemUserQuery || person?.email_work || person?.display_name || "");
    setSystemUsers(res.data?.data || []);
  } catch (err) {
    console.error("Failed to search system users:", err);
    alert(err?.response?.data?.message || "Failed to search system users.");
  } finally {
    setSearchingUsers(false);
  }
}

async function handleAutoLinkUser() {
  try {
    setLinkingUser(true);
    await hrApi.autoLinkPersonUser(id);
    await load();
  } catch (err) {
    console.error("Failed to auto-link user:", err);
    alert(err?.response?.data?.message || "Failed to auto-link user.");
  } finally {
    setLinkingUser(false);
  }
}

async function handleManualLinkUser(userId) {
  try {
    setLinkingUser(true);
    await hrApi.linkPersonUser(id, { user_id: userId });
    await load();
  } catch (err) {
    console.error("Failed to link user:", err);
    alert(err?.response?.data?.message || "Failed to link user.");
  } finally {
    setLinkingUser(false);
  }
}

async function handleUnlinkUser() {
  const confirmed = window.confirm("Unlink system user from this person?");
  if (!confirmed) return;

  try {
    setLinkingUser(true);
    await hrApi.unlinkPersonUser(id);
    await load();
  } catch (err) {
    console.error("Failed to unlink user:", err);
    alert(err?.response?.data?.message || "Failed to unlink user.");
  } finally {
    setLinkingUser(false);
  }
}

async function handleUploadDocumentFile(e) {
  const file = e.target.files?.[0];
  if (!file) return;

  try {
    setUploadingFile(true);

    const formData = new FormData();
    formData.append("file", file);

    const res = await hrApi.uploadDocumentFile(formData);
    const fileData = res.data?.data;

    setDocumentForm((s) => ({
      ...s,
      file_url: fileData?.file_url || "",
      file_name: fileData?.file_name || "",
    }));
  } catch (err) {
    console.error("Failed to upload file:", err);
    alert(err?.response?.data?.message || "Failed to upload file.");
  } finally {
    setUploadingFile(false);
  }
}

async function handleCloseEmployment(e) {
  e.preventDefault();

  if (!closingEmploymentId) return;
  if (!closeEmploymentForm.end_date) {
    alert("End date is required.");
    return;
  }

  try {
    setSavingEmployment(true);

    await hrApi.closeEmployment(closingEmploymentId, {
      end_date: closeEmploymentForm.end_date,
      notes: closeEmploymentForm.notes || null,
    });

    setClosingEmploymentId(null);
    setCloseEmploymentForm({
      end_date: "",
      notes: "",
    });

    await load();
  } catch (err) {
    console.error("Failed to close employment:", err);
    alert(err?.response?.data?.message || "Failed to close employment.");
  } finally {
    setSavingEmployment(false);
  }
}

async function handleCreateDocument(e) {
  e.preventDefault();

  try {
    setSavingDocument(true);

    if (!documentForm.title.trim()) {
      alert("Title is required.");
      return;
    }

    if (!documentForm.file_url.trim()) {
      alert("File URL is required.");
      return;
    }

    await hrApi.createDocument({
      person_id: Number(id),
      document_type: documentForm.document_type,
      title: documentForm.title.trim(),
      file_url: documentForm.file_url.trim(),
      file_name: documentForm.file_name || null,
      issue_date: documentForm.issue_date || null,
      expiry_date: documentForm.expiry_date || null,
      visibility_scope: documentForm.visibility_scope || "hr_only",
      is_confidential: Number(documentForm.is_confidential || 0),
      notes: documentForm.notes || null,
    });

    setShowDocumentModal(false);
    setDocumentForm({
      document_type: "employment_contract",
      title: "",
      file_url: "",
      file_name: "",
      issue_date: "",
      expiry_date: "",
      visibility_scope: "hr_only",
      is_confidential: 0,
      notes: "",
    });

    await load();
  } catch (err) {
    console.error("Failed to create document:", err);
    alert(err?.response?.data?.message || "Failed to create document.");
  } finally {
    setSavingDocument(false);
  }
}



async function handleCreateRole(e) {
  e.preventDefault();

  try {
    setSavingRole(true);

    if (!person?.position_id) {
      alert("This person has no current position.");
      return;
    }

    if (!roleForm.role_key.trim()) {
      alert("Role key is required.");
      return;
    }

    if (!roleForm.role_name.trim()) {
      alert("Role name is required.");
      return;
    }

    await hrApi.createPositionRole({
      position_id: Number(person.position_id),
      role_key: roleForm.role_key.trim(),
      role_name: roleForm.role_name.trim(),
      role_scope: roleForm.role_scope || "business",
      active_flag: Number(roleForm.active_flag || 1),
    });

    setShowRoleModal(false);
    setRoleForm({
      role_key: "",
      role_name: "",
      role_scope: "business",
      valid_from: "",
      valid_to: "",
      active_flag: 1,
      notes: "",
    });

    await load();
  } catch (err) {
    console.error("Failed to create role:", err);
    alert(err?.response?.data?.message || "Failed to create role.");
  } finally {
    setSavingRole(false);
  }
}


async function handleDeleteRole(roleId) {
  const confirmed = window.confirm("Are you sure you want to delete this role?");
  if (!confirmed) return;

  try {
    await hrApi.deletePositionRole(roleId);
    await load();
  } catch (err) {
    console.error("Failed to delete role:", err);
    alert(err?.response?.data?.message || "Failed to delete role.");
  }
}

async function load() {
  try {
    setLoading(true);

    const personRes = await hrApi.getPersonById(id);
    const personData = personRes.data?.data || null;

    const [
      employmentRes,
      notesRes,
      documentsRes,
      rolesRes,
    ] = await Promise.all([
      hrApi.getEmploymentByPersonId(id),
      hrApi.getNotesByPersonId(id),
      hrApi.getDocumentsByPersonId(id),
      personData?.position_id
        ? hrApi.getPositionRoles(personData.position_id)
        : Promise.resolve({ data: { data: [] } }),
    ]);

    setPerson(personData);
    setEmployment(employmentRes.data?.data || []);
    setNotes(notesRes.data?.data || []);
    setDocuments(documentsRes.data?.data || []);
    setRoles(rolesRes.data?.data || []);
  } catch (err) {
    console.error("Failed to load person profile:", err);
  } finally {
    setLoading(false);
  }
}

  useEffect(() => {
    load();
  }, [id]);

  async function handleAddNote(e) {
  e.preventDefault();

  if (!newNote.trim()) return;

  try {
    setSavingNote(true);

    await hrApi.createNote({
      person_id: Number(id),
      note_type: "general",
      note_text: newNote.trim(),
      visibility: "hr_only",
    });

    setNewNote("");
    await load();
  } catch (err) {
    console.error("Failed to create note:", err);
    alert(err?.response?.data?.message || "Failed to create note.");
  } finally {
    setSavingNote(false);
  }
}

  async function handleCreateEmployment(e) {
  e.preventDefault();

  try {
    setSavingEmployment(true);

    const payload = {
      person_id: Number(id),
      employment_type: employmentForm.employment_type,
      contract_type: employmentForm.contract_type || null,
      employment_status: employmentForm.employment_status || "active",
      start_date: employmentForm.start_date,
      end_date: employmentForm.end_date || null,
      probation_end_date: employmentForm.probation_end_date || null,
      work_capacity_percent: employmentForm.work_capacity_percent ? Number(employmentForm.work_capacity_percent) : 100,
      working_hours_per_week: employmentForm.working_hours_per_week ? Number(employmentForm.working_hours_per_week) : null,
      salary_type: employmentForm.salary_type || null,
      currency: employmentForm.currency || null,
      gross_salary: employmentForm.gross_salary ? Number(employmentForm.gross_salary) : null,
      net_salary: employmentForm.net_salary ? Number(employmentForm.net_salary) : null,
      cost_center_code: employmentForm.cost_center_code || null,
      legal_entity: employmentForm.legal_entity || null,
      office_location: employmentForm.office_location || null,
      work_mode: employmentForm.work_mode || null,
      active_flag: Number(employmentForm.active_flag || 1),
      notes: employmentForm.notes || null,
    };

    if (!payload.start_date) {
      alert("Start date is required.");
      return;
    }

    await hrApi.createEmployment(payload);

    setShowEmploymentModal(false);
    setEmploymentForm({
      employment_type: "employee",
      contract_type: "full_time",
      employment_status: "active",
      start_date: "",
      end_date: "",
      probation_end_date: "",
      work_capacity_percent: 100,
      working_hours_per_week: 40,
      salary_type: "",
      currency: "",
      gross_salary: "",
      net_salary: "",
      cost_center_code: "",
      legal_entity: "",
      office_location: "",
      work_mode: "onsite",
      active_flag: 1,
      notes: "",
    });

    await load();
  } catch (err) {
    console.error("Failed to create employment:", err);
    alert(err?.response?.data?.message || "Failed to create employment.");
  } finally {
    setSavingEmployment(false);
  }
}

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Učitavanje...</div>;
  }

  if (!person) {
    return <div className="p-6 text-sm text-red-500">Person not found.</div>;
  }

  const activeEmployment = employment.find((e) => Number(e.active_flag) === 1) || employment[0];

  if (accessLoading) {
  return <div className="p-6">Loading access...</div>;
}

if (!canSeeHR || !canReadPeople) {
  return <div className="p-6 text-red-600">Access denied.</div>;
}

  return (
  <div className="p-6 space-y-6">
    <HRPersonHeader
  person={person}
  activeEmployment={activeEmployment}
  onAddEmployment={canWritePeople ? () => setShowEmploymentModal(true) : undefined}
  onAddDocument={canWritePeople ? () => setShowDocumentModal(true) : undefined}
  onAddRole={
    canWritePeople
      ? () => {
          if (!person?.position_id) {
            alert("This person has no current position.");
            return;
          }
          setShowRoleModal(true);
        }
      : undefined
  }
  onCloseEmployment={
    canWritePeople ? (record) => setClosingEmploymentId(record?.id) : undefined
  }
/>

    <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Linked System User</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Central link between HR person and app login user. Auto-assign and notifications use this link.
          </p>
        </div>

        <div>
          {person?.user_id ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900">
              Linked
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900">
              Not linked
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 text-sm">
        <div>
          <div className="text-gray-500 dark:text-gray-400 mb-1">Work email</div>
          <div>{person?.email_work || "-"}</div>
        </div>

        <div>
          <div className="text-gray-500 dark:text-gray-400 mb-1">Linked user ID</div>
          <div>{person?.user_id || "-"}</div>
        </div>

        <div>
          <div className="text-gray-500 dark:text-gray-400 mb-1">Status</div>
          <div>{person?.user_id ? "Assignable" : "Not assignable"}</div>
        </div>
      </div>

      {canWritePeople && (
        <div className="mt-5 space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <button
              type="button"
              onClick={handleAutoLinkUser}
              disabled={linkingUser}
              className="px-4 py-2 rounded-xl bg-black text-white disabled:opacity-60"
            >
              {linkingUser ? "Working..." : "Auto-link by email"}
            </button>

            {person?.user_id ? (
              <button
                type="button"
                onClick={handleUnlinkUser}
                disabled={linkingUser}
                className="px-4 py-2 rounded-xl border border-red-300 text-red-700 bg-red-50 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900 disabled:opacity-60"
              >
                Unlink
              </button>
            ) : null}
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
            <div className="text-sm font-medium">Manual link</div>

            <div className="flex flex-col md:flex-row gap-3">
              <input
                value={systemUserQuery}
                onChange={(e) => setSystemUserQuery(e.target.value)}
                placeholder="Search system users by email, name or uid..."
                className="w-full border border-gray-300 rounded-xl px-3 py-2 bg-white text-black dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
              />

              <button
                type="button"
                onClick={handleSearchSystemUsers}
                disabled={searchingUsers}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
              >
                {searchingUsers ? "Searching..." : "Search"}
              </button>
            </div>

            <div className="space-y-2">
              {systemUsers.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  No loaded candidates yet.
                </div>
              ) : (
                systemUsers.map((u) => (
                  <div
                    key={u.uid}
                    className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-xl border border-gray-200 dark:border-neutral-800 px-3 py-3"
                  >
                    <div className="text-sm">
                      <div className="font-medium">{u.display_name || "-"}</div>
                      <div className="text-gray-500 dark:text-gray-400">{u.email || "-"}</div>
                      <div className="text-gray-400 dark:text-gray-500 text-xs mt-1">{u.uid}</div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleManualLinkUser(u.uid)}
                      disabled={linkingUser}
                      className="px-4 py-2 rounded-xl bg-black text-white disabled:opacity-60"
                    >
                      Link this user
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>

    <HRProfileTabs
      person={person}
      activeEmployment={activeEmployment}
      employment={employment}
      documents={documents}
      roles={roles}
      notes={notes}
      newNote={newNote}
      setNewNote={setNewNote}
      savingNote={savingNote}
      onCreateNote={canWritePeople ? handleAddNote : undefined}
      onOpenEmploymentModal={canWritePeople ? () => setShowEmploymentModal(true) : undefined}
      onOpenDocumentModal={canWritePeople ? () => setShowDocumentModal(true) : undefined}
      onOpenRoleModal={canWritePeople ? () => setShowRoleModal(true) : undefined}
      onOpenCloseEmployment={canWritePeople ? (record) => setClosingEmploymentId(record?.id) : undefined}
      onDeleteRole={canWritePeople ? handleDeleteRole : undefined}
    />

{canWritePeople && showEmploymentModal && (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
    <div className="bg-white dark:bg-neutral-950 text-black dark:text-white rounded-2xl shadow-xl w-full max-w-5xl p-6 max-h-[90vh] overflow-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Add employment record</h2>
        <button onClick={() => setShowEmploymentModal(false)} className="text-gray-500">
          ✕
        </button>
      </div>

      <form onSubmit={handleCreateEmployment} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <UISelect
            value={employmentForm.employment_type}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, employment_type: e.target.value }))}
          >
            <option value="employee">Employee</option>
            <option value="contractor">Contractor</option>
            <option value="freelancer">Freelancer</option>
            <option value="intern">Intern</option>
            <option value="consultant">Consultant</option>
            <option value="external_partner">External partner</option>
          </UISelect>

          <UISelect
            value={employmentForm.contract_type}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, contract_type: e.target.value }))}
          >
            <option value="full_time">Full time</option>
            <option value="part_time">Part time</option>
            <option value="temporary">Temporary</option>
            <option value="probation">Probation</option>
            <option value="fixed_term">Fixed term</option>
            <option value="indefinite">Indefinite</option>
            <option value="project_based">Project based</option>
            <option value="service_contract">Service contract</option>
            <option value="student_contract">Student contract</option>
            <option value="other">Other</option>
          </UISelect>

          <UISelect
            value={employmentForm.employment_status}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, employment_status: e.target.value }))}
          >
            <option value="active">Active</option>
            <option value="probation">Probation</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
            <option value="ended">Ended</option>
          </UISelect>

          <UISelect
            value={employmentForm.work_mode}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, work_mode: e.target.value }))}
          >
            <option value="onsite">Onsite</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </UISelect>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm text-gray-500 mb-1">Start date</label>
            <UIInput 
              type="date"
              value={employmentForm.start_date}
              onChange={(e) => setEmploymentForm((s) => ({ ...s, start_date: e.target.value }))}
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">End date</label>
            <UIInput 
              type="date"
              value={employmentForm.end_date}
              onChange={(e) => setEmploymentForm((s) => ({ ...s, end_date: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">Probation end</label>
            <UIInput 
              type="date"
              value={employmentForm.probation_end_date}
              onChange={(e) => setEmploymentForm((s) => ({ ...s, probation_end_date: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">Weekly hours</label>
            <UIInput 
              type="number"
              value={employmentForm.working_hours_per_week}
              onChange={(e) => setEmploymentForm((s) => ({ ...s, working_hours_per_week: e.target.value }))}
            />
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4">
  <div className="text-sm font-medium mb-3">Current organization</div>

  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
    <div>
      <div className="text-gray-500 mb-1">Department</div>
      <div>{person?.department_name || "-"}</div>
    </div>

    <div>
      <div className="text-gray-500 mb-1">Team</div>
      <div>{person?.team_name || "-"}</div>
    </div>

    <div>
      <div className="text-gray-500 mb-1">Position</div>
      <div>{person?.position_title || "-"}</div>
    </div>

    <div>
      <div className="text-gray-500 mb-1">Manager</div>
      <div>{person?.manager_name || "-"}</div>
    </div>
  </div>
</div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <UIInput
            placeholder="Legal entity"
            value={employmentForm.legal_entity}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, legal_entity: e.target.value }))}
          />
          <UIInput
            placeholder="Office location"
            value={employmentForm.office_location}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, office_location: e.target.value }))}
          />
          <UIInput
            placeholder="Cost center"
            value={employmentForm.cost_center_code}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, cost_center_code: e.target.value }))}
          />
          <UIInput
            type="number"
            placeholder="Capacity %"
            value={employmentForm.work_capacity_percent}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, work_capacity_percent: e.target.value }))}
          />
          <UISelect
            value={employmentForm.salary_type}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, salary_type: e.target.value }))}
          >
            <option value="">Salary type</option>
            <option value="gross">Gross</option>
            <option value="net">Net</option>
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="project_based">Project based</option>
          </UISelect>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <UIInput
            placeholder="Currency"
            value={employmentForm.currency}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, currency: e.target.value }))}
          />
          <UIInput
            type="number"
            placeholder="Gross salary"
            value={employmentForm.gross_salary}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, gross_salary: e.target.value }))}
          />
          <UIInput
            type="number"
            placeholder="Net salary"
            value={employmentForm.net_salary}
            onChange={(e) => setEmploymentForm((s) => ({ ...s, net_salary: e.target.value }))}
          />
        </div>

        <UITextarea
          placeholder="Notes"
          value={employmentForm.notes}
          onChange={(e) => setEmploymentForm((s) => ({ ...s, notes: e.target.value }))}
        />

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowEmploymentModal(false)}
            className="px-4 py-2 rounded-xl border"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={savingEmployment}
            className="px-4 py-2 rounded-xl bg-black text-white"
          >
            {savingEmployment ? "Saving..." : "Save employment"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{canWritePeople && closingEmploymentId && (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
    <div className="bg-white dark:bg-neutral-950 text-black dark:text-white rounded-2xl shadow-xl w-full max-w-5xl p-6 max-h-[90vh] overflow-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Close employment</h2>
        <button
  onClick={() => {
    setClosingEmploymentId(null);
    setCloseEmploymentForm({ end_date: "", notes: "" });
  }}
  className="text-gray-500"
>
  ✕
</button>
      </div>

      <form onSubmit={handleCloseEmployment} className="space-y-4">
        <div>
          <label className="block text-sm text-gray-500 mb-1">End date</label>
          <UIInput 
            type="date"
            value={closeEmploymentForm.end_date}
            onChange={(e) => setCloseEmploymentForm((s) => ({ ...s, end_date: e.target.value }))}
            required
          />
        </div>

        <div>
          <label className="block text-sm text-gray-500 mb-1">Notes</label>
          <UITextarea
            value={closeEmploymentForm.notes}
            onChange={(e) => setCloseEmploymentForm((s) => ({ ...s, notes: e.target.value }))}
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setClosingEmploymentId(null)}
            className="px-4 py-2 rounded-xl border"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={savingEmployment}
            className="px-4 py-2 rounded-xl bg-red-600 text-white"
          >
            {savingEmployment ? "Closing..." : "Close employment"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{canWritePeople && showDocumentModal && (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
    <div className="bg-white dark:bg-neutral-950 text-black dark:text-white rounded-2xl shadow-xl w-full max-w-5xl p-6 max-h-[90vh] overflow-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Add document</h2>
        <button onClick={() => setShowDocumentModal(false)} className="text-gray-500">
          ✕
        </button>
      </div>

      <form onSubmit={handleCreateDocument} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <UISelect
            value={documentForm.document_type}
            onChange={(e) => setDocumentForm((s) => ({ ...s, document_type: e.target.value }))}
          >
            <option value="employment_contract">Employment contract</option>
            <option value="contract_annex">Contract annex</option>
            <option value="id_document">ID document</option>
            <option value="nda">NDA</option>
            <option value="certificate">Certificate</option>
            <option value="medical_exam">Medical exam</option>
            <option value="training_record">Training record</option>
            <option value="hr_form">HR form</option>
            <option value="other">Other</option>
          </UISelect>

          <UISelect
            value={documentForm.visibility_scope}
            onChange={(e) => setDocumentForm((s) => ({ ...s, visibility_scope: e.target.value }))}
          >
            <option value="public_internal">Public internal</option>
            <option value="manager_only">Manager only</option>
            <option value="hr_only">HR only</option>
            <option value="confidential">Confidential</option>
          </UISelect>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <UIInput
            placeholder="Title"
            value={documentForm.title}
            onChange={(e) => setDocumentForm((s) => ({ ...s, title: e.target.value }))}
            required
          />
          <UIInput
            placeholder="File name"
            value={documentForm.file_name}
            onChange={(e) => setDocumentForm((s) => ({ ...s, file_name: e.target.value }))}
          />
        </div>

        <div>
  <label className="block text-sm text-gray-500 mb-1">Upload file</label>
  <UIInput
    type="file"
    onChange={handleUploadDocumentFile}
  />
  {uploadingFile && (
    <div className="text-sm text-gray-500 mt-2">Uploading...</div>
  )}
</div>

        <UIInput
  placeholder="File URL (auto-filled after upload or enter manually)"
  value={documentForm.file_url}
  onChange={(e) => setDocumentForm((s) => ({ ...s, file_url: e.target.value }))}
  required
/>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-500 mb-1">Issue date</label>
            <UIInput 
              type="date"
              value={documentForm.issue_date}
              onChange={(e) => setDocumentForm((s) => ({ ...s, issue_date: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1">Expiry date</label>
            <UIInput 
              type="date"
              value={documentForm.expiry_date}
              onChange={(e) => setDocumentForm((s) => ({ ...s, expiry_date: e.target.value }))}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <UIInput 
            type="checkbox"
            checked={Number(documentForm.is_confidential) === 1}
            onChange={(e) =>
              setDocumentForm((s) => ({
                ...s,
                is_confidential: e.target.checked ? 1 : 0,
              }))
            }
          />
          Confidential document
        </label>

        <UITextarea
          placeholder="Notes"
          value={documentForm.notes}
          onChange={(e) => setDocumentForm((s) => ({ ...s, notes: e.target.value }))}
        />

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowDocumentModal(false)}
            className="px-4 py-2 rounded-xl border"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={savingDocument}
            className="px-4 py-2 rounded-xl bg-black text-white"
          >
            {savingDocument ? "Saving..." : "Save document"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{canWritePeople && showRoleModal && (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
    <div className="bg-white dark:bg-neutral-950 text-black dark:text-white rounded-2xl shadow-xl w-full max-w-5xl p-6 max-h-[90vh] overflow-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Add position role</h2>
        <button onClick={() => setShowRoleModal(false)} className="text-gray-500">
          ✕
        </button>
      </div>

      <form onSubmit={handleCreateRole} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <UIInput
            placeholder="Role key"
            value={roleForm.role_key}
            onChange={(e) => setRoleForm((s) => ({ ...s, role_key: e.target.value }))}
            required
          />
          <UIInput
            placeholder="Role name"
            value={roleForm.role_name}
            onChange={(e) => setRoleForm((s) => ({ ...s, role_name: e.target.value }))}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <UISelect
            value={roleForm.role_scope}
            onChange={(e) => setRoleForm((s) => ({ ...s, role_scope: e.target.value }))}
          >
            <option value="system">System</option>
            <option value="module">Module</option>
            <option value="business">Business</option>
          </UISelect>

          <UIInput
            type="date"
            value={roleForm.valid_from}
            onChange={(e) => setRoleForm((s) => ({ ...s, valid_from: e.target.value }))}
          />

          <UIInput
            type="date"
            value={roleForm.valid_to}
            onChange={(e) => setRoleForm((s) => ({ ...s, valid_to: e.target.value }))}
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <UIInput 
            type="checkbox"
            checked={Number(roleForm.active_flag) === 1}
            onChange={(e) =>
              setRoleForm((s) => ({
                ...s,
                active_flag: e.target.checked ? 1 : 0,
              }))
            }
          />
          Active role
        </label>

        <UITextarea
          placeholder="Notes"
          value={roleForm.notes}
          onChange={(e) => setRoleForm((s) => ({ ...s, notes: e.target.value }))}
        />

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowRoleModal(false)}
            className="px-4 py-2 rounded-xl border"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={savingRole}
            className="px-4 py-2 rounded-xl bg-black text-white"
          >
            {savingRole ? "Saving..." : "Save role"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

    </div>
  );
}